import type { KbReadinessKey, KbReadinessStatus } from '../../types/api';
import type { KnowledgeBaseSection } from '../knowledge-base/sections';

/**
 * The readiness rows' contract order, and the brand-profile tab that fills
 * each one. Shared by the full card and Home's one-line summary so the two
 * always name the same gap and open the same tab. Labels and nudges follow the
 * brand's kind and live in `brandKind.ts`.
 */
export const READINESS_ORDER: readonly KbReadinessKey[] = [
  'what_you_do',
  'who_its_for',
  'tone',
  'tagline',
  'logo',
  'never_say',
  'voice_examples',
  'products',
  'proof_points',
  'assets',
  'inspiration',
];

export const READINESS_SECTIONS: Readonly<Record<KbReadinessKey, KnowledgeBaseSection>> = {
  what_you_do: 'brand-identity',
  who_its_for: 'brand-identity',
  tone: 'brand-identity',
  tagline: 'brand-identity',
  logo: 'brand-identity',
  never_say: 'brand-identity',
  voice_examples: 'voice',
  products: 'products',
  proof_points: 'proof-points',
  assets: 'assets',
  inspiration: 'inspiration',
};

/** Rows whose gap the nudge names ahead of any other, when they are short. */
const LEAD_NUDGES: ReadonlySet<KbReadinessKey> = new Set([
  'what_you_do',
  'voice_examples',
  'proof_points',
]);

/**
 * The gap the nudge line names: the first short row with a lead nudge, else
 * the first short row, in the order given. Null when every row is complete.
 */
export function pickReadinessGap<T extends { key: KbReadinessKey; status: KbReadinessStatus }>(
  items: readonly T[],
): T | null {
  const short = items.filter((item) => item.status !== 'complete');
  return short.find((item) => LEAD_NUDGES.has(item.key)) ?? short[0] ?? null;
}
